import { ChangeDetectorRef, NgZone } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import type { NotificationData } from '../../services/notification.service';
import type { NotificationService } from '../../services/notification.service';
import type { Order } from '../models/order.models';
import {
  headerDropdownNotificationsCache,
  resolveLprEntryIdFromListItem,
  resolveLprEntryIdFromNotificationData,
} from '../../notifications/notification.utils';
import {
  extractLprRowPayloadFromListNotification,
  getLicensePlateNumberFromNotification,
} from '../../shared/utils/notification-display.util';

export interface OrdersLprPlateUpdateContext {
  getOrders: () => Order[];
  setOrders: (orders: Order[]) => void;
  onOrderPatched?: (order: Order, previousPlate: string) => void;
  markForCheck?: () => void;
}

function normalizeOrderIdRef(value: any): string {
  return String(value ?? '')
    .replace(/^#+/, '')
    .trim();
}

function flattenLprPusherRow(data: Record<string, any>): Record<string, any> {
  const nested = data['data'];
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return { ...data, ...(nested as Record<string, any>) };
  }
  return { ...data };
}

/**
 * Same cache merge as the header notification dropdown — resolves order_id from the
 * linked LPR row when the Pusher patch payload only carries plate + lpr_entry_id.
 */
function resolveMergedLprRowFromNotification(notification: NotificationData): Record<string, any> {
  headerDropdownNotificationsCache.upsertFromNotification(notification);
  const direct = flattenLprPusherRow((notification.data ?? {}) as Record<string, any>);
  const lprId = resolveLprEntryIdFromNotificationData(direct);
  if (!lprId) {
    return direct;
  }
  const cached = headerDropdownNotificationsCache.items.find(
    item => resolveLprEntryIdFromListItem(item) === lprId
  );
  if (!cached) {
    return direct;
  }
  const cachedRow = extractLprRowPayloadFromListNotification(cached.data);
  return { ...cachedRow, ...direct };
}

function collectOrderMatchIds(row: Record<string, any>): Set<string> {
  const ids = new Set<string>();
  const add = (value: any) => {
    const normalized = normalizeOrderIdRef(value);
    if (normalized) {
      ids.add(normalized);
    }
  };
  add(row['order_id']);
  add(row['orderId']);
  add(row['public_order_id']);
  add(row['publicOrderId']);
  return ids;
}

function orderMatchesLprRow(order: Order, row: Record<string, any>): boolean {
  const lprId = resolveLprEntryIdFromNotificationData(row);
  if (lprId && (order.lprId === lprId || order.lprEntryId === lprId)) {
    return true;
  }

  const matchIds = collectOrderMatchIds(row);
  if (matchIds.size === 0) {
    return false;
  }

  const rowIds = [
    normalizeOrderIdRef(order.id),
    normalizeOrderIdRef(order.orderId),
    normalizeOrderIdRef(order.barCode),
  ].filter(Boolean);
  return rowIds.some(id => matchIds.has(id));
}

/** Fleet plates may include a leading state prefix in the payload — strip for display when applicable. */
export function normalizeFleetLicensePlateForDisplay(
  plate: string,
  event?: string,
  stateCode?: string
): string {
  if (!plate) {
    return '';
  }
  if (event !== 'fleet-vehicle-found') {
    return plate;
  }

  const cleanedPlate = String(plate).trim();
  const cleanedState = String(stateCode || '')
    .trim()
    .toUpperCase();

  if (cleanedState.length >= 2 && cleanedPlate.toUpperCase().startsWith(cleanedState)) {
    return cleanedPlate.slice(cleanedState.length).trim();
  }
  return cleanedPlate;
}

/** Orders table uses the same display plate shape as order/list API and tunnel LPR updates. */
export function resolveDisplayPlateFromLprPayload(data: Record<string, any>): string {
  const event = String(data['event'] ?? '').trim();
  const state = String(data['state'] ?? data['lpr_state'] ?? '').trim();
  const payloadForDisplay = {
    ...data,
    state: data['state'] ?? data['lpr_state'] ?? '',
  };

  const displayPlate = String(data['display_license_plate'] ?? '').trim();
  if (displayPlate) {
    return normalizeFleetLicensePlateForDisplay(
      displayPlate,
      event || undefined,
      state || undefined
    ).toUpperCase();
  }

  const stripped = getLicensePlateNumberFromNotification(payloadForDisplay);
  if (!stripped || stripped === 'Unknown') {
    return '';
  }
  return normalizeFleetLicensePlateForDisplay(
    stripped,
    event || undefined,
    state || undefined
  ).toUpperCase();
}

export function updateOrderVehicleInfoPlate(vehicleInfo: string, newPlate: string): string {
  if (!newPlate) {
    return vehicleInfo;
  }
  if (vehicleInfo.includes('(')) {
    return vehicleInfo.replace(/\([^)]*\)\s*$/, `(${newPlate})`);
  }
  return `${vehicleInfo} (${newPlate})`.trim();
}

export function findOrderIndexForLprPlateUpdate(orders: Order[], row: Record<string, any>): number {
  return orders.findIndex(order => orderMatchesLprRow(order, row));
}

export function patchOrderListLicensePlateFromLprPayload(
  orders: Order[],
  row: Record<string, any>
): { orders: Order[]; patched: boolean; updatedOrder?: Order; previousPlate?: string } {
  const index = findOrderIndexForLprPlateUpdate(orders, row);
  if (index < 0) {
    return { orders, patched: false };
  }

  const newPlate = resolveDisplayPlateFromLprPayload(row);
  if (!newPlate) {
    return { orders, patched: false };
  }

  const existing = orders[index];
  const prevPlate = (existing.licensePlate || '').toUpperCase();
  if (newPlate === prevPlate) {
    return { orders, patched: false };
  }

  const updated: Order = {
    ...existing,
    licensePlate: newPlate,
    vehicleInfo: updateOrderVehicleInfoPlate(existing.vehicleInfo, newPlate),
  };

  return {
    orders: [...orders.slice(0, index), updated, ...orders.slice(index + 1)],
    patched: true,
    updatedOrder: updated,
    previousPlate: prevPlate,
  };
}

export function applyInPlaceOrdersLprPlateUpdateFromNotification(
  ctx: OrdersLprPlateUpdateContext,
  notification: NotificationData
): boolean {
  const row = resolveMergedLprRowFromNotification(notification);
  const result = patchOrderListLicensePlateFromLprPayload(ctx.getOrders(), row);
  if (!result.patched || !result.updatedOrder) {
    return false;
  }

  ctx.setOrders(result.orders);
  ctx.onOrderPatched?.(result.updatedOrder, result.previousPlate ?? '');
  ctx.markForCheck?.();
  return true;
}

/** Subscribe to NotificationService LPR corrections — same source as notification modal / list. */
export function subscribeToOrdersLprPlateUpdates(
  notificationService: NotificationService,
  destroy$: Subject<void>,
  ctx: OrdersLprPlateUpdateContext,
  ngZone: NgZone
): void {
  notificationService.lprEntryUpdatedNotification$.pipe(takeUntil(destroy$)).subscribe(value => {
    if (!value) return;
    const { notification } = value;
    ngZone.run(() => applyInPlaceOrdersLprPlateUpdateFromNotification(ctx, notification));
  });
}

/** Convenience for components that inject ChangeDetectorRef. */
export function ordersLprPlateUpdateMarkForCheck(cdr: ChangeDetectorRef): () => void {
  return () => cdr.markForCheck();
}
